import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { settingsService } from '../services/settings';
import { auth } from '../config/firebase';

interface DarkModeContextType {
  isDarkMode: boolean;
  toggleDarkMode: () => Promise<void>;
}

const DarkModeContext = createContext<DarkModeContextType | undefined>(undefined);

export const DarkModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Initialize with the same logic as the early script in index.html
    const raw = localStorage.getItem('darkMode');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    // Use localStorage if explicitly set, otherwise fall back to OS preference
    const shouldBeDark = raw !== null ? (raw === 'true') : prefersDark;
    
    // Ensure the class is applied on initial render
    if (shouldBeDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    return shouldBeDark;
  });
  const { user } = useAuth();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const loadUserSettings = async () => {
      if (!isInitialized && user?.uid) {
        try {
          const settings = await settingsService.getUserSettings(user.uid);
          const localStorageValue = localStorage.getItem('darkMode');
          
          // Only sync from Firestore if localStorage isn't already set
          // This prevents overriding the user's current session preference
          if (localStorageValue === null) {
            setIsDarkMode(settings.darkMode);
            if (settings.darkMode) {
              document.documentElement.classList.add('dark');
            } else {
              document.documentElement.classList.remove('dark');
            }
            localStorage.setItem('darkMode', String(settings.darkMode));
          }
          setIsInitialized(true);
        } catch (error) {
          console.error('Error loading user settings:', error);
          setIsInitialized(true);
        }
      }
    };

    loadUserSettings();
  }, [user, isInitialized]);

  const toggleDarkMode = async () => {
    if (!user?.uid) return;

    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Update localStorage
    localStorage.setItem('darkMode', String(newDarkMode));
    
    // Sync with user settings if authenticated
    if (user?.uid) {
      await settingsService.toggleDarkMode(user.uid, newDarkMode);
    }
  };

  const value = {
    isDarkMode,
    toggleDarkMode
  };

  return (
    <DarkModeContext.Provider value={value}>
      {children}
    </DarkModeContext.Provider>
  );
};

export const useDarkMode = () => {
  const context = useContext(DarkModeContext);
  if (context === undefined) {
    throw new Error('useDarkMode must be used within a DarkModeProvider');
  }
  return context;
};
